import { computed, makeObservable, observable, runInAction } from 'mobx';
import { RootStore } from './index';
import { BaseStore } from './base.store';
import { IStore } from '../shared/common';
import {ADMIN_ROLE, EXTERNAL_USER_SOURCE, READ_USER_ROLE} from '../../utils/constants';
import { UserDto } from '../../dtos/user.dto';
import userService from '../../services/user-service';
import { BasicCustomerData, ServiceDto } from '../../dtos/customer.dto';
import { EventType } from 'src/dtos/analytics.dto';
import analyticsService from 'src/services/analytics.service';
import customerService from '../../services/customer.service';

export class UserStore extends BaseStore implements IStore<UserDto>{

    public data: UserDto | undefined = undefined;
    public isLoading: boolean = false;
    public isSignOutSystemCalled: boolean = false;

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            data: observable,
            isLoading: observable,
            getUserFirstNameOrLast: computed,
            userCustomers: computed,
            emailPrefix: computed,
            fullName: computed,
            email: computed,
            isReadOnlyUser: computed,
            isExternalUser: computed,
            isReportSubscriptionAllowed: computed,
            isPersonalViewVisible: computed,
            isApiKeyTabVisible: computed,
            isPolicyRecommendationTabVisible: computed
        });
    }

    public async load() {
        try {
            this.isLoading = true;
            const res = await userService.loadUser();
            runInAction(() => {
                this.data = res;
                this.isLoading = false;
                analyticsService.registerUser(res);
            });
        }
        catch (e) {
            this.isLoading = false;
            throw e;
        }
    }

    public async logout() {
        runInAction(() => {
            this.isSignOutSystemCalled = true;
        });
        await userService.logout();
    }

    get isAuthenticated() {
        return !!this.data;
    }

    get getTheOnlyCustomerOfExternalUser() {
        return (this.isExternalUser && this.data?.allowedCustomers?.length === 1) ?
            this.data?.allowedCustomers[0].customerId : undefined;
    }

    public getServiceListOfSelectedCustomer(customerId: number): ServiceDto[] {
        const selectedCustomer = this.data?.allowedCustomers?.find(customer => customer.customerId === customerId);
        return selectedCustomer?.services || [];
    }

    public getSingleServiceOfSelectedCustomerIfExist(customerId: number): ServiceDto | undefined {
        const serviceList = this.getServiceListOfSelectedCustomer(customerId);
        return serviceList.length === 1 ? serviceList[0] : undefined;
    }

    get getUserFirstNameOrLast() {
        return this.data?.firstName || this.data?.lastName;
    }

    get emailPrefix() {
        return this.data?.email?.split('@')[0] || '';
    }

    get email() {
        return this.data?.email || '';
    }

    get fullName() {
        if (!this.data) {
            return '';
        }
        const { firstName, lastName } = this.data;
        if (!firstName || !lastName) {
            return '';
        }
        const capitalizedFirstName = firstName.charAt(0).toUpperCase() + firstName.slice(1);
        const capitalizedLastName = lastName.charAt(0).toUpperCase() + lastName.slice(1);
        return `${capitalizedFirstName} ${capitalizedLastName}`;
    }

    get getUserNameAcronyms() {
        if (!this.data) return '';

        const { firstName, lastName } = this.data;
        if (!firstName && !lastName) return '';

        return `${firstName?.charAt(0).toLocaleUpperCase() || ''}${lastName?.charAt(0).toLocaleUpperCase() || ''}`;
    }

    get userCustomers(): BasicCustomerData[] {
        return this.data?.allowedCustomers ??
            this.rootStore.customerStore.data!;
    }

    public isUnauthorizedUser(): boolean {
        return this.data?.allowedCustomers?.length === 0;
    }

    public isAdminUser(): boolean {
        return !!(this.data?.selectedRole?.toLowerCase().includes(ADMIN_ROLE));
    }

    get isReadOnlyUser(): boolean {
        return !!(this.data?.selectedRole?.toLowerCase().includes(READ_USER_ROLE));
    }

    public async getServiceById(customerId: number, serviceId: number): Promise<ServiceDto | undefined> {
        const services = await customerService.getAllCustomerServices(customerId);
        return services.find(service => service.serviceId === serviceId);
    }

    public sendIdentifyEvent() {
        this.sendEvent(EventType.LogOut);
        analyticsService.resetUser();
    }

    get isExternalUser(): boolean {
        return this.data?.source === EXTERNAL_USER_SOURCE;
    }

    get isReportSubscriptionAllowed(): boolean {
        return this.isExternalUser ? true : window.CONFIG?.ALLOW_SUBSCRIPTION_TO_INTERNAL_USERS === true;
    }

    get isPersonalViewVisible() {
        return this.isReportSubscriptionAllowed && this.rootStore.customerStore.hasNonBronzeConnection;
    }

    get isApiKeyTabVisible() {
        return window.CONFIG?.ENABLE_API_KEY && this.isExternalUser && this.rootStore.customerStore.hasNonBronzeConnection;
    }

    get isPolicyRecommendationTabVisible() {
        return window.CONFIG?.ENABLE_ML && this.rootStore.customerStore.hasNonBronzeConnection;
    }
}
